import React, { useState, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { API_URL } from '../config';
import { GET_SERVICE_TYPES } from '../graphql/queries';

const productVariationSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Name is required'),
  price: z.number().min(0, 'Price must be positive')
});

const productSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title is too long'),
  description: z.string().min(1, 'Description is required').max(1000, 'Description is too long'),
  imageUrl: z.string().optional(),
  basePrice: z.number().min(0, 'Price must be positive'),
  variations: z.array(productVariationSchema).optional(),
  isActive: z.boolean().default(true),
  serviceTypeId: z.string().nullable().optional(),
  availableFromMonth: z.number().min(1).max(12).nullable().optional(),
  availableFromDay: z.number().min(1).max(31).nullable().optional(),
  availableToMonth: z.number().min(1).max(12).nullable().optional(),
  availableToDay: z.number().min(1).max(31).nullable().optional(),
  availableForAllProperties: z.boolean().default(true),
  propertyIds: z.array(z.string()).optional()
}).refine((data) => {
  // If any availability field is filled, all must be filled
  const hasAnyDate = data.availableFromMonth || data.availableFromDay || data.availableToMonth || data.availableToDay;
  const hasAllDates = data.availableFromMonth && data.availableFromDay && data.availableToMonth && data.availableToDay;
  
  if (hasAnyDate && !hasAllDates) {
    return false;
  }
  return true;
}, {
  message: "Please fill in all availability fields or leave them all empty",
  path: ["availability"]
});

export default function ProductForm({ initialData, onSubmit, isLoading }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [imagePreview, setImagePreview] = useState(initialData?.imageUrl || null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [availableProperties, setAvailableProperties] = useState([
    { id: '1', name: 'Property A', address: '123 Main St' },
    { id: '2', name: 'Property B', address: '456 Oak Ave' },
    { id: '3', name: 'Property C', address: '789 Pine Rd' },
  ]);
  const [filteredProperties, setFilteredProperties] = useState([]);

  const { data: serviceTypesData } = useQuery(GET_SERVICE_TYPES);
  const activeServiceTypes = (serviceTypesData?.serviceTypes || []).filter(st => st.isActive);
  
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      imageUrl: initialData?.imageUrl || '',
      basePrice: initialData?.basePrice || null,
      variations: initialData?.variations || [],
      isActive: initialData?.isActive ?? true,
      serviceTypeId: initialData?.serviceTypeId || null,
      availableFromMonth: initialData?.availableFromMonth || null,
      availableFromDay: initialData?.availableFromDay || null,
      availableToMonth: initialData?.availableToMonth || null,
      availableToDay: initialData?.availableToDay || null,
      availableForAllProperties: initialData?.availableForAllProperties ?? true,
      propertyIds: initialData?.propertyIds || []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variations'
  });

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Create a preview
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get presigned URL from your API
      const response = await fetch(`${API_URL}/api/products/get-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // This sends HTTP-only cookies automatically
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get upload URL');
      }

      const { uploadUrl, imageUrl } = await response.json();
      setUploadProgress(20);

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file to S3: ${uploadResponse.statusText}`);
      }

      setUploadProgress(90);

      // Update form with the new image URL
      setValue('imageUrl', imageUrl);
      setUploadProgress(100);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress(0);
      setImagePreview(null);
      // Show a more user-friendly error message
      toast.error(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [setValue]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleFormSubmit = async (data) => {
    try {
      await onSubmit(data);
      if (!initialData) {
        reset(); // Only reset form if it's a create operation
      }
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling will be managed by the parent component
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {initialData ? 'Edit Product' : 'Create New Product'}
          </h2>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
          <div className="space-y-4">
            {/* Title Field */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Product Title
              </label>
              <input
                id="title"
                type="text"
                {...register('title')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                aria-invalid={errors.title ? 'true' : 'false'}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.title.message}</p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                {...register('description')}
                className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                aria-invalid={errors.description ? 'true' : 'false'}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.description.message}</p>
              )}
            </div>

            {/* Image Upload Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Image
              </label>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-sky-500 bg-sky-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                {imagePreview ? (
                  <div className="space-y-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mx-auto max-h-48 rounded-lg"
                    />
                    <p className="text-sm text-gray-500">
                      Click or drag to replace image
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="text-sm text-gray-600">
                      <span className="text-sky-600 hover:text-sky-500">
                        Click to upload
                      </span>{' '}
                      or drag and drop
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>
              {isUploading && (
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-sky-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Base Price Field */}
            <div>
              <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-1">
                Base Price
              </label>
              <div className="relative rounded-lg">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <Controller
                  name="basePrice"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      step="0.01"
                      id="basePrice"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                      className="pl-7 w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                      placeholder="0.00"
                      aria-invalid={errors.basePrice ? 'true' : 'false'}
                    />
                  )}
                />
              </div>
              {errors.basePrice && (
                <p className="mt-1 text-sm text-red-600" role="alert">{errors.basePrice.message}</p>
              )}
            </div>

            {/* Availability Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Available From</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="availableFromMonth" className="block text-sm font-medium text-gray-700 mb-1">
                      Month
                    </label>
                    <Controller
                      name="availableFromMonth"
                      control={control}
                      render={({ field }) => (
                        <select
                          id="availableFromMonth"
                          className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        >
                          <option value="">Select Month</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(2024, i, 1).toLocaleString('default', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label htmlFor="availableFromDay" className="block text-sm font-medium text-gray-700 mb-1">
                      Day
                    </label>
                    <Controller
                      name="availableFromDay"
                      control={control}
                      render={({ field }) => (
                        <select
                          id="availableFromDay"
                          className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        >
                          <option value="">Select Day</option>
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700">Available To</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="availableToMonth" className="block text-sm font-medium text-gray-700 mb-1">
                      Month
                    </label>
                    <Controller
                      name="availableToMonth"
                      control={control}
                      render={({ field }) => (
                        <select
                          id="availableToMonth"
                          className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        >
                          <option value="">Select Month</option>
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(2024, i, 1).toLocaleString('default', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                  <div>
                    <label htmlFor="availableToDay" className="block text-sm font-medium text-gray-700 mb-1">
                      Day
                    </label>
                    <Controller
                      name="availableToDay"
                      control={control}
                      render={({ field }) => (
                        <select
                          id="availableToDay"
                          className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        >
                          <option value="">Select Day</option>
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {errors.availability && (
              <div className="mt-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>{errors.availability.message}</span>
                </div>
              </div>
            )}

            {/* Product Variants */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Product Variants</h3>
                <button
                  type="button"
                  onClick={() => append({ name: '', price: 0 })}
                  className="text-sm bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Variant
                </button>
              </div>

              {fields.map((field, index) => (
                <div key={field.id} className="border border-gray-200 rounded-lg p-4 space-y-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 space-y-4">
                      <div>
                        <label htmlFor={`variations.${index}.name`} className="block text-sm font-medium text-gray-700 mb-1">
                          Variant Name
                        </label>
                        <input
                          type="text"
                          {...register(`variations.${index}.name`)}
                          className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                          aria-invalid={errors.variations?.[index]?.name ? 'true' : 'false'}
                        />
                        {errors.variations?.[index]?.name && (
                          <p className="mt-1 text-sm text-red-600" role="alert">
                            {errors.variations[index]?.name?.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor={`variations.${index}.price`} className="block text-sm font-medium text-gray-700 mb-1">
                          Variant Price
                        </label>
                        <div className="relative rounded-lg">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">$</span>
                          </div>
                          <Controller
                            name={`variations.${index}.price`}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="number"
                                step="0.01"
                                value={field.value}
                                onChange={(e) => field.onChange(parseFloat(e.target.value))}
                                className="pl-7 w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                                aria-invalid={errors.variations?.[index]?.price ? 'true' : 'false'}
                              />
                            )}
                          />
                        </div>
                        {errors.variations?.[index]?.price && (
                          <p className="mt-1 text-sm text-red-600" role="alert">
                            {errors.variations[index]?.price?.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="text-red-600 hover:text-red-700"
                      aria-label="Remove variant"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Property Availability Section */}
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Property Availability</h3>
                    <p className="text-sm text-gray-500">
                      Choose whether to apply this product to all properties or select specific ones
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-full px-4 py-2 border border-gray-200">
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Apply to All Properties
                    </span>
                    <Controller
                      name="availableForAllProperties"
                      control={control}
                      render={({ field }) => (
                        <button
                          type="button"
                          onClick={() => {
                            field.onChange(!field.value);
                            if (!field.value) {
                              // If enabling "all properties", clear specific selections
                              setValue('propertyIds', []);
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                            field.value ? 'bg-sky-600' : 'bg-gray-200'
                          }`}
                          role="switch"
                          aria-checked={field.value}
                        >
                          <span className="sr-only">Apply to all properties</span>
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              field.value ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      )}
                    />
                  </div>
                </div>

                <Controller
                  name="propertyIds"
                  control={control}
                  render={({ field }) => {
                    const selectedProperties = availableProperties.filter(p => 
                      field.value.includes(p.id)
                    );
                    
                    const handleSearch = (e) => {
                      const term = e.target.value.toLowerCase();
                      setSearchTerm(term);
                      setFilteredProperties(
                        availableProperties.filter(p => 
                          (p.name.toLowerCase().includes(term) || 
                           p.address.toLowerCase().includes(term)) &&
                          !field.value.includes(p.id)
                        )
                      );
                    };

                    const handleSelect = (propertyId) => {
                      field.onChange([...field.value, propertyId]);
                      setSearchTerm('');
                      setFilteredProperties([]);
                    };

                    const handleRemove = (propertyId) => {
                      field.onChange(field.value.filter(id => id !== propertyId));
                    };

                    return (
                      <div className={watch('availableForAllProperties') ? 'opacity-50 pointer-events-none' : ''}>
                        <div className="relative">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder="Search properties..."
                            className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                            disabled={watch('availableForAllProperties')}
                          />
                          {searchTerm && filteredProperties.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-auto">
                              {filteredProperties.map(property => (
                                <button
                                  key={property.id}
                                  type="button"
                                  onClick={() => handleSelect(property.id)}
                                  className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                                >
                                  <div className="font-medium">{property.name}</div>
                                  <div className="text-sm text-gray-500">{property.address}</div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedProperties.map(property => (
                            <div
                              key={property.id}
                              className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded-full px-3 py-1 text-sm"
                            >
                              <span>{property.name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemove(property.id)}
                                className="text-gray-400 hover:text-gray-600"
                                aria-label={`Remove ${property.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }}
                />
              </div>
            </div>

            {/* Job Configuration */}
            <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Job Configuration</h3>
                  <p className="text-sm text-gray-500">
                    Assign a service type so a job can be created when this product's order is approved
                  </p>
                </div>
                <Controller
                  name="serviceTypeId"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="serviceTypeId"
                      className="w-full rounded-lg border border-gray-200 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 px-3 py-2"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      aria-label="Select service type for job creation"
                    >
                      <option value="">No job (select a service type)</option>
                      {activeServiceTypes.map(st => (
                        <option key={st.id} value={st.id}>
                          {st.title}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {watch('serviceTypeId') && (
                  <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>When an order for this product is approved, a "Create Job" button will appear allowing you to create a <strong>{activeServiceTypes.find(st => st.id === watch('serviceTypeId'))?.title}</strong> job.</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isLoading || isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {(isLoading || isSubmitting) && (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isLoading || isSubmitting ? 'Saving...' : initialData ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 